/**
 * ErrorBoundary — catches a render crash in a page and shows a friendly message
 * instead of white-screening the whole app. Keyed by route in AppLayout so
 * navigating to another page clears the error automatically.
 *
 * When an `onError` reporter is provided (AppLayout wires one that POSTs to
 * /client-errors), the crash also lands in the agency console's feedback
 * queue automatically — nobody has to remember what they were doing and type
 * it into "Submit a bug". The reporter call is wrapped so a broken reporter
 * can never crash the crash screen.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  /** Optional crash reporter — receives the error + React's component stack. */
  onError?: (error: Error, info: { componentStack: string | null }) => void;
}
interface State { error: Error | null }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Surface with context; the onError reporter is the prod error sink.
    console.error("UI crash caught by ErrorBoundary:", error);
    try {
      this.props.onError?.(error, { componentStack: errorInfo.componentStack ?? null });
    } catch {
      // The reporter must never crash the crash screen.
    }
  }

  render(): ReactNode {
    if (this.state.error) {
      return (
        <div style={{ padding: "40px 8px", color: "var(--text)", maxWidth: 520 }}>
          <h2 style={{ marginTop: 0, fontSize: 20 }}>Something broke on this page.</h2>
          <p style={{ color: "var(--body)", fontSize: 14, lineHeight: 1.5 }}>
            The rest of the app still works — switch pages from the sidebar, or reload. If it
            keeps happening, tell us what you were doing and we’ll fix it.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              border: "1px solid var(--border)", background: "var(--surface-2)", color: "var(--text)",
              borderRadius: "var(--r-sm)", padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", marginTop: 6,
            }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
